import React, { useState, useMemo, useEffect, useCallback } from "react";
import styled, { css, keyframes } from "styled-components";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { makeStyles, Modal, CircularProgress, Button } from "@material-ui/core";
import AWSService from "../../../services/AWSService";
import { getErrorMessage, getMimeType } from "../../../helpers/functions";
import { Popper } from "@material-ui/core";
import CaseService from "../../../services/CaseService";
import JSZip from "jszip";
import CustomSelect from "../../common/CustomSelect/CustomSelect";
import PartyService from "../../../services/PartyService";
import debounce from "lodash/debounce";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import {
  OptionContainer,
  OptionName,
  OptionEmail,
  OptionCheck,
  OptionContent,
  StyledAutocomplete,
  SelectLabel,
  SelectWrapper,
} from "../NoticeGeneration/component/CustomSelect";
import { CustomInputField } from "../../common/FormInputs";
import useSettings from "../../../hooks/useSettings";

const PAGE_SIZE = 50;

const VALID_FILE_FORMATS = [
  "pdf",
  "xlsx",
  "csv",
  "png",
  "jpeg",
  "text",
  "docx",
  "xls",
  "tiff",
  "mp3",
  "doc",
];

const VALID_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/tiff",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "video/mp4",
  "audio/mpeg",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
  "text/plain",
  "application/msword",
];

const FOLDER_TYPES = [
  { label: "Select a folder", value: "folder" },
  { label: "Select a ZIP file", value: "zipfile" },
];

const VIEWING_OPTIONS = [
  {
    label: "Share with parties, CM & Neutral",
    value: "all",
    desc: "Visible to all participants",
  },
  {
    label: "Share with Neutral only",
    value: "others",
    desc: "Neutral mediator only",
  },
  {
    label: "Private viewing only",
    value: "me",
    desc: "Only you can view",
  },
];

const fadeUp = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const scaleIn = keyframes`from{opacity:0;transform:scale(.96)}to{opacity:1;transform:scale(1)}`;
const shimmer = keyframes`0%{background-position:-200% center}100%{background-position:200% center}`;
const pulseTeal = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(0,131,140,.25)}50%{box-shadow:0 0 0 8px rgba(0,131,140,0)}`;

const useStyles = makeStyles(() => ({
  button: {
    backgroundColor: COLORS.PRIMARY_WHITE,
    boxShadow: "none",
    width: "77px",
    height: "30px",
    color: COLORS.BTN_GREEN,
    textTransform: "Capitalize",
    border: "solid 1px #00838c",
    fontFamily: theme.fonts.primaryFontBold,
    fontSize: 14,
  },
  formLabel: {
    fontFamily: theme.fonts.primaryFontSemiBold,
    color: COLORS.COLOR_DARK,
  },
  table: {
    backgroundColor: "transparent",
    border: "1px solid #e1e3ee",
    borderRadius: 7,
    "& .MuiTableCell-root": {
      borderBottom: 0,
    },
  },
  tableHead: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: COLORS.INPUT_LABEL,
    fontSize: 12,
  },
  tableBody: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: COLORS.COLOR_DARK,
    fontSize: 12,
  },
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    "& li[data-option-index]": {
      padding: "12px 16px !important",
      borderBottom: "1px solid #f3f4f6",
      transition: "all 0.2s ease",

      "&:hover": {
        backgroundColor: "#f0f9ff",
        paddingLeft: "20px !important",
      },
    },
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
    padding: "16px",
    fontSize: "14px",
  },
  paper: {
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1) !important",
    borderRadius: "8px !important",
    border: "1px solid #e2e8f0 !important",
    width: "450px",
  },
}));

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const chunkArray = (arr, n) =>
  arr.reduce(
    (acc, _, i) => (i % n === 0 ? [...acc, arr.slice(i, i + n)] : acc),
    [],
  );

const parseNotUploaded = (data) =>
  data.map((item) => {
    const [id, ...rest] = item.split(" - ");
    return { id, filename: rest.join(" - ") };
  });

const BulkUpload = ({
  bulkUpload,
  setBulkUpload,
  cmList,
  cmId,
  menuItems = [],
}) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [urls, setUrls] = useState([]);
  const [view, setView] = useState("me");
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [folderType, setFolderType] = useState("folder");
  const [notUploaded, setNotUploaded] = useState([]);
  const [checkerId, setCheckerId] = useState();
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [partySearchOpen, setPartySearchOpen] = useState(false);
  const [partyValue, setPartyValue] = useState(null);
  const [partyError, setPartyError] = useState("");
  const { selectMenuItems } = useSettings();
  const [noticeCategory, setNoticeCategory] = useState("");

  const debouncedFetch = useMemo(
    () =>
      debounce(async (term, page) => {
        try {
          setLoading(true);
          const q = `?search=${encodeURIComponent(term)}&page=${page}&perPage=${PAGE_SIZE}&status=active`;
          const res = await PartyService.index(q);
          if (res?.data?.length) {
            const mapped = res.data.map(({ name, id, owner }) => ({
              label: name,
              value: id,
              email: owner?.email,
            }));
            setOptions((p) => (page === 1 ? mapped : [...p, ...mapped]));
            setTotal(res.meta?.total || total);
          }
        } catch (err) {
          enqueueSnackbar(getErrorMessage(err), { variant: "error" });
          if (err.response?.status >= 500)
            setTimeout(() => debouncedFetch(term, page), 1000);
        } finally {
          setLoading(false);
        }
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [total],
  );

  useEffect(() => {
    if (partySearchOpen && inputValue.length >= 2) {
      setOptions([]);
      debouncedFetch(inputValue, 1);
    } else if (!inputValue) {
      setOptions([]);
    }
  }, [inputValue, partySearchOpen, debouncedFetch]);

  const s3Single = useCallback(
    async (file, folderName, pct) => {
      try {
        setLoader({
          state: true,
          message: `Uploading ${folderName} — ${Math.round(pct)}%`,
        });
        const s3 = await AWSService.getS3URL({ key: file.name });
        if (s3.url) {
          const res = await AWSService.uploadToS3(s3.url, file);
          setUrls((p) => [...p, res]);
        }
      } catch (err) {
        enqueueSnackbar(getErrorMessage(err), { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    },
    [setLoader, enqueueSnackbar],
  );

  const s3Batch = useCallback(
    async (batches) => {
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        try {
          setLoader({
            state: true,
            message: `Uploading batch ${i + 1} of ${batches.length}`,
          });
          const s3 = await AWSService.getS3URL({
            key: batch.map((f) => f.name),
          });
          const bUrls = await Promise.all(
            batch.map((f, idx) => AWSService.uploadToS3(s3.url[idx], f)),
          );
          setUrls((p) => [...p, ...bUrls]);
        } catch (err) {
          enqueueSnackbar(getErrorMessage(err), { variant: "error" });
        } finally {
          setLoader({ state: false });
        }
      }
    },
    [setLoader, enqueueSnackbar],
  );

  const handleFolderSelection = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    setInvalidFiles([]);
    // Group files into folders
    const folderStructure = selectedFiles.reduce((acc, file) => {
      const folderName = file.webkitRelativePath.split("/")[1]; // Adjust index for deeper structures
      if (!acc[folderName]) acc[folderName] = [];
      acc[folderName].push(file);
      return acc;
    }, {});

    // Start uploading files immediately
    await uploadFiles(folderStructure);
  };

  /**
   * @description split folder
   */

  const uploadFiles = async (folderStructure) => {
    let count = 0;
    for (const [folderName, files] of Object.entries(folderStructure)) {
      count++;
      let percentage = (count / Object.keys(folderStructure).length) * 100;
      for (const file of files) {
        let webkitdirectoryName;
        if (VALID_MIME_TYPES.includes(file.type)) {
          if (file.webkitRelativePath) {
            webkitdirectoryName = file.webkitRelativePath.split("/");
            webkitdirectoryName =
              webkitdirectoryName[webkitdirectoryName.length - 2];
          }
          const newFileName = `${webkitdirectoryName}/${file?.name}`;
          // const newFileName = `${folderName}-${file?.name}`;
          const modifiedFile = new File([file], newFileName, {
            lastModified: file.lastModified,
            lastModifiedDate: file.lastModifiedDate,
            name: newFileName,
            type: file.type,
            size: file?.size,
            webkitRelativePath: file?.webkitRelativePath,
          });
          await s3Single(modifiedFile, folderName, percentage);
        } else {
          const fileToNotUploaded = `${folderName} - ${file?.name}`;
          setNotUploaded((prevState) => [...prevState, fileToNotUploaded]);
          setInvalidFiles((prevState) => [...prevState, file?.name]);
        }
      }
    }
    enqueueSnackbar("All files uploaded successfully!", {
      variant: "success",
    });
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";

    if (!file) return;

    const isZip =
      file.name.toLowerCase().endsWith(".zip") ||
      file.type === "application/zip" ||
      file.type === "application/x-zip-compressed";

    if (!isZip) {
      enqueueSnackbar(
        `"${file.name}" is not a ZIP file. Please select a valid .zip file.`,
        { variant: "error" },
      );
      return;
    }

    setLoader({ state: true, message: "Extracting ZIP file…" });
    setUrls([]);
    setInvalidFiles([]);

    const zip = new JSZip();
    const content = await zip.loadAsync(file);
    const folderFiles = {};
    for (const path in content.files) {
      const entry = content.files[path];
      if (!entry.dir) {
        const folder = path.includes("/") ? path.split("/")[0] : "root";
        const blob = await entry.async("blob");
        if (!folderFiles[folder]) folderFiles[folder] = [];
        folderFiles[folder].push(new File([blob], entry.name));
      }
    }

    const valid = [];
    for (const folder in folderFiles) {
      for (const f of folderFiles[folder]) {
        const ext = f.name.split(".").pop();
        if (VALID_FILE_FORMATS.includes(ext)) {
          const parts = f.name.split("/");
          const fname = parts[parts.length - 1];
          const dir = parts.length > 1 ? parts[parts.length - 2] : undefined;
          const newName = dir ? `${dir}/${fname}` : fname;
          valid.push(
            new File([f], newName, {
              type: getMimeType(ext),
              lastModified: f.lastModified,
            }),
          );
        } else {
          setInvalidFiles((p) => [...p, f.name]);
        }
      }
    }

    const batches = chunkArray(valid, 50);
    if (!batches.length) {
      setLoader({ state: false });
      enqueueSnackbar(
        "Please upload only valid file formats inside the ZIP file",
        { variant: "error" },
      );
      return;
    }
    setLoader({ state: false });
    await s3Batch(batches);
  };

  const handleSubmit = async (values) => {
    try {
      setLoader({ state: true, message: "Please wait…" });
      const payload = {
        url: { url: urls },
        filesNotUploaded: notUploaded,
        visibilityType: view,
        checkerId,
        partyId: partyValue?.value,
        noticeCategory: noticeCategory,
      };
      const res = await CaseService.bulkUploadDocuments(payload);
      if (res) enqueueSnackbar(res.message, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: "error" });
    } finally {
      setLoader({ state: false });
      setBulkUpload(false);
      setUrls([]);
      setInvalidFiles([]);
    }
  };

  const handleNext = () => {
    // if (!partyValue) {
    //   setPartyError("Please select a party to continue");
    //   return;
    // }
    setPartyError("");
    setConfirmOpen(true);
  };

  return (
    <Modal open={bulkUpload} onClose={() => setBulkUpload(false)}>
      <Overlay
        onClick={(e) => e.target === e.currentTarget && setBulkUpload(false)}
      >
        <Card wide={confirmOpen}>
          <Header>
            <HeaderLeft>
              <Title>Bulk Upload</Title>
              <Subtitle>
                {confirmOpen
                  ? "Review before submitting"
                  : "Upload documents in bulk"}
              </Subtitle>
            </HeaderLeft>
            <HeaderRight>
              <StepPips>
                <Pip $active={!confirmOpen} />
                <Pip $active={confirmOpen} />
              </StepPips>
              <CloseBtn onClick={() => setBulkUpload(false)}>✕</CloseBtn>
            </HeaderRight>
          </Header>

          {confirmOpen ? (
            <>
              <Body>
                <Banner>
                  <BannerText>
                    <BannerTitle>
                      {notUploaded.length
                        ? `${notUploaded.length} file(s) skipped due to format issues`
                        : `${urls.length} file(s) ready to upload`}
                    </BannerTitle>
                    <BannerDesc>
                      {notUploaded.length
                        ? "You can still proceed with the valid files listed above."
                        : "All files passed validation. Click Submit to continue."}
                    </BannerDesc>
                  </BannerText>
                </Banner>

                {notUploaded.length > 0 && (
                  <Section>
                    <SLabel>Files with issues</SLabel>
                    <TableWrap>
                      <Row
                        style={{
                          background: "#f8fafc",
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      >
                        <TH>#</TH>
                        <TH>Contract No.</TH>
                        <TH>File Name</TH>
                      </Row>
                      {parseNotUploaded(notUploaded).map((row, i) => (
                        <Row key={i}>
                          <TD>{i + 1}</TD>
                          <TD>{row.id}</TD>
                          <TD>{row.filename}</TD>
                        </Row>
                      ))}
                    </TableWrap>
                  </Section>
                )}
              </Body>

              <Footer>
                <Btn $variant="secondary" onClick={() => setConfirmOpen(false)}>
                  Back
                </Btn>
                <Btn
                  $variant="primary"
                  onClick={handleSubmit}
                  disabled={!urls.length}
                >
                  Submit {urls.length > 0 && `(${urls.length} files)`}
                </Btn>
              </Footer>
            </>
          ) : (
            <>
              <Body>
                <Section>
                  <SLabel>Upload Method</SLabel>
                  <ChipRow>
                    {FOLDER_TYPES.map((ft) => (
                      <Chip
                        key={ft.value}
                        $active={folderType === ft.value}
                        onClick={() => setFolderType(ft.value)}
                        type="button"
                      >
                        {ft.label}
                      </Chip>
                    ))}
                  </ChipRow>
                </Section>
                <Section>
                  <SLabel>Visibility</SLabel>
                  <VisList>
                    {VIEWING_OPTIONS.map((opt) => (
                      <VisCard
                        key={opt.value}
                        $active={view === opt.value}
                        onClick={() => setView(opt.value)}
                      >
                        <VisBody>
                          <VisTitle>{opt.label}</VisTitle>
                          <VisDesc>{opt.desc}</VisDesc>
                        </VisBody>
                        <RadioRing $active={view === opt.value} />
                      </VisCard>
                    ))}
                  </VisList>
                </Section>
                <Section>
                  <SelectWrapper className="form-fields">
                    <SelectLabel
                      style={{
                        fontFamily: "openSans-SemiBold, sans-serif",
                        fontSize: 11,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: ".8px",
                        color: "#64748b",
                      }}
                    >
                      Select a Party (Optional)
                    </SelectLabel>
                    <StyledAutocomplete
                      classes={{
                        listbox: classes.listbox,
                        noOptions: classes.noOptions,
                        paper: classes.paper,
                      }}
                      open={partySearchOpen}
                      onOpen={() => setPartySearchOpen(true)}
                      onClose={() => setPartySearchOpen(false)}
                      value={partyValue}
                      onChange={(_, val) => {
                        setPartyValue(val);
                        // setPartyError(val ? "" : "Party is required");
                      }}
                      inputValue={inputValue}
                      onInputChange={(_, val) => setInputValue(val)}
                      selectOnFocus
                      clearOnBlur
                      options={options}
                      loading={loading}
                      noOptionsText={
                        inputValue.length < 2
                          ? "Type at least 2 characters to search"
                          : loading
                            ? "Searching…"
                            : "No parties found"
                      }
                      getOptionLabel={(o) => o.label?.toString() ?? ""}
                      renderOption={(o) => (
                        <OptionContainer>
                          <OptionContent>
                            <OptionName>{o.label}</OptionName>
                            <OptionEmail>{o.email}</OptionEmail>
                          </OptionContent>
                          <OptionCheck>✓</OptionCheck>
                        </OptionContainer>
                      )}
                      renderInput={(params) => (
                        <CustomInputField
                          {...params}
                          variant="outlined"
                          placeholder="Type to search parties…"
                          error={Boolean(partyError)}
                          helperText={partyError}
                          onKeyDown={(e) => {
                            if (
                              (e.key === "Enter" || e.key === "Tab") &&
                              !partyValue
                            ) {
                              setPartyError("Party is required");
                              e.preventDefault();
                            }
                          }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading && (
                                  <CircularProgress color="inherit" size={18} />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </SelectWrapper>
                </Section>

                <Section>
                  <SelectWrapper>
                    <SelectLabel
                      style={{
                        fontFamily: "openSans-SemiBold, sans-serif",
                        fontSize: 11,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: ".8px",
                        color: "#64748b",
                      }}
                    >
                      Notice Category Name
                    </SelectLabel>

                    <StyledAutocomplete
                      id="notice-category-autocomplete"
                      classes={{
                        listbox: classes.listbox,
                        noOptions: classes.noOptions,
                        paper: classes.paper,
                      }}
                      size="small"
                      options={
                        selectMenuItems?.length
                          ? selectMenuItems
                          : [{ label: "None", value: "none" }]
                      }
                      groupBy={(option) => option.category}
                      getOptionLabel={(option) => option.name}
                      PopperComponent={CustomPopper}
                      renderInput={(params) => (
                        <CustomInputField
                          {...params}
                          placeholder="Search notice category"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      renderGroup={(params) => (
                        <div style={{ padding: "8px 0" }}>
                          <div
                            style={{
                              padding: "10px 16px",
                              fontWeight: "700",
                              fontSize: "11px",
                              color: "#0f766e",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              backgroundColor: "#f0fdf4",
                              borderRadius: "6px",
                              margin: "4px 8px",
                            }}
                          >
                            {params.group}
                          </div>

                          <div
                            style={{
                              padding: "4px 0",
                              color: COLORS.COLOR_DARK,
                              fontFamily: theme.fonts.primaryFontSemiBold,
                            }}
                          >
                            {params.children}
                          </div>
                        </div>
                      )}
                      noOptionsText={
                        <div
                          style={{
                            color: COLORS.INPUT_LABEL,
                            fontFamily: "openSans-SemiBold, sans-serif",
                            fontSize: "14px",
                            padding: "8px",
                          }}
                        >
                          No document type found
                        </div>
                      }
                      onChange={(event, newValue) =>
                        setNoticeCategory(newValue?.name || "")
                      }
                    />
                  </SelectWrapper>
                </Section>
                {/* .................. */}

                <Section>
                  <SLabel>Checker (Optional)</SLabel>
                  <CustomSelect
                    white
                    id="checkerId"
                    menuItemValues={cmList.filter((i) => i?.value !== cmId)}
                    name="checkerId"
                    value={checkerId}
                    onChange={(e) => setCheckerId(e?.target?.value)}
                    style={{ width: "100%" }}
                  />
                </Section>
                <Section>
                  <SLabel>Documents</SLabel>
                  <DropZone $filled={urls.length > 0}>
                    {urls.length > 0 ? (
                      <>
                        <DropCount>{urls.length} Files Ready</DropCount>
                        <DropText>Click to replace or add more files</DropText>
                      </>
                    ) : (
                      <>
                        <DropText>
                          {folderType === "zipfile"
                            ? "Click to select a ZIP file"
                            : "Click to select a folder"}
                        </DropText>
                        <DropHint>
                          pdf · mp3 · mp4 · docx · csv · txt · xlsx · xls · png
                          · jpeg · tiff
                        </DropHint>
                      </>
                    )}
                    <Button
                      style={{
                        inset: 0,
                        opacity: 0,
                        cursor: "pointer",
                        width: "100%",
                        height: "100%",
                      }}
                      variant="contained"
                      component="label"
                    >
                      {folderType === "zipfile" ? (
                        <input
                          type="file"
                          accept="application/zip"
                          multiple
                          style={{ display: "none" }}
                          onChange={handleZipUpload}
                        />
                      ) : (
                        <input
                          type="file"
                          webkitdirectory="true"
                          multiple
                          style={{ display: "none" }}
                          onChange={handleFolderSelection}
                        />
                      )}
                    </Button>
                  </DropZone>

                  {invalidFiles.length > 0 && (
                    <WarnTag>
                      {invalidFiles.length} file(s) skipped — unsupported format
                    </WarnTag>
                  )}
                </Section>
              </Body>

              <Footer>
                <Btn $variant="secondary" onClick={() => setBulkUpload(false)}>
                  Cancel
                </Btn>
                <Btn
                  $variant="primary"
                  onClick={handleNext}
                  disabled={!urls.length}
                >
                  Review & Submit
                </Btn>
              </Footer>
            </>
          )}
        </Card>
      </Overlay>
    </Modal>
  );
};

export default BulkUpload;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  animation: ${fadeUp} 0.2s ease;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: ${({ wide }) => (wide ? "680px" : "520px")};
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.07),
    0 24px 56px -12px rgba(0, 0, 0, 0.22);
  animation: ${scaleIn} 0.26s cubic-bezier(0.34, 1.56, 0.64, 1);
  transition: max-width 0.3s ease;
`;

const Header = styled.div`
  padding: 22px 28px;
  background: linear-gradient(135deg, #00838c 0%, #005f66 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div``;

const Title = styled.h2`
  margin: 0;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.3px;
`;

const Subtitle = styled.p`
  margin: 3px 0 0;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepPips = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 5px 12px;
`;
const Pip = styled.div`
  width: ${({ active }) => (active ? "20px" : "8px")};
  height: 8px;
  border-radius: 6px;
  background: ${({ active }) => (active ? "#fff" : "rgba(255,255,255,.35)")};
  transition: all 0.3s ease;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  animation: ${fadeUp} 0.3s ease;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
  }
`;

const Footer = styled.div`
  padding: 16px 28px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const SLabel = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #64748b;
`;

const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  border-radius: 10px;
  border: 1.5px solid ${({ $active }) => ($active ? "#00838c" : "#e2e8f0")};
  background: ${({ $active }) => ($active ? "#f0fdfa" : "#fff")};
  color: ${({ $active }) => ($active ? "#00838c" : "#64748b")};
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  ${({ $active }) =>
    $active &&
    css`
      animation: ${pulseTeal} 1.5s ease-out 1;
    `}
  &:hover {
    border-color: #00838c;
    color: #00838c;
    background: #f0fdfa;
  }
`;

const VisList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const VisCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 16px;
  border-radius: 12px;
  border: 1.5px solid ${({ $active }) => ($active ? "#00838c" : "#e2e8f0")};
  background: ${({ $active }) => ($active ? "#f0fdfa" : "#fafafa")};
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: #00838c;
    background: #f0fdfa;
  }
`;
const VisBody = styled.div`
  flex: 1;
`;
const VisTitle = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #293461;
`;
const VisDesc = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
`;
const RadioRing = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 2px solid ${({ $active }) => ($active ? "#00838c" : "#cbd5e1")};
  background: ${({ $active }) => ($active ? "#00838c" : "transparent")};
  position: relative;
  transition: all 0.15s;
  &::after {
    content: "";
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: #fff;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    transition: opacity 0.15s;
  }
`;

const DropZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 32px 20px;
  border-radius: 14px;
  background: ${({ $filled }) => ($filled ? "#f0fdfa" : "#fafafa")};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  ${({ $filled }) =>
    $filled &&
    css`
      &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(0, 131, 140, 0.06) 50%,
          transparent 100%
        );
        background-size: 200% auto;
        animation: ${shimmer} 2s linear infinite;
      }
    `}
  &:hover {
    border-color: #00838c;
    background: #f0fdfa;
  }
`;

const DropCount = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #00838c;
  z-index: 1;
`;
const DropText = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 13px;
  color: #64748b;
  z-index: 1;
  text-align: center;
`;
const DropHint = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  color: #94a3b8;
  z-index: 1;
  text-align: center;
`;

const WarnTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 7px 12px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 12px;
  color: #c2410c;
  font-weight: 700;
`;

const Banner = styled.div`
  background: linear-gradient(135deg, #f0fdfa, #e6f7f8);
  border: 1px solid #99d9dd;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const BannerText = styled.div``;

const BannerTitle = styled.p`
  margin: 0 0 3px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #0f4c52;
`;
const BannerDesc = styled.p`
  margin: 0;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 12px;
  color: #2d8a92;
`;

const TableWrap = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  max-height: 280px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
  }
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 50px 1fr 2fr;
  padding: 10px 16px;
  transition: background 0.1s;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f8fafc;
  }
`;
const TH = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
`;
const TD = styled.div`
  font-family: openSans-SemiBold, sans-serif;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  word-break: break-all;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 10px 22px;
  border-radius: 10px;
  font-family: openSans-SemiBold, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 2px solid transparent;
  outline: none;
  ${({ $variant }) =>
    $variant === "primary"
      ? css`
          background: #00838c;
          color: #ffffff;
          &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
            transform: none;
          }
        `
      : css`
          background: #d91b1b;
          border-color: #d91b1b;
          color: #ffffff;
        `}
`;
